import { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import moment from 'moment';
import classnames from 'classnames';

import { BannerMessageV2 } from 'ui-components/components/banner-message/banner-message-v2';
import { BannerStatus } from 'ui-components/components/banner-message/banner-enums';
import { DatePicker } from 'ui-components/components/date-picker';
import { FlexColumn, FlexRow } from 'ui-components/components/flex';
import { FloatTextArea } from 'ui-components/components/float-text-area';
import { Input, version2 } from 'ui-components/components/input';
import { Text, TextType } from 'ui-components/components/text';
import { TimePicker } from 'ui-components/components/time-picker';
import { INLINE_MESSAGE_TYPES } from 'ui-components/components/inline-message';
import { Divider } from 'ui-components/components/divider';

import { CARRIER_CONTACT, TENANT_CONTACT, DELIVERY_NOTE } from '../constants';

import { messages } from './messages';
import style from './schedule-freight.module.less';

// 8:00 AM
const START_TIME = 8;
// 4:00 PM
const END_TIME = 16;
// 30 minute increments
const MINUTE_INCREMENT_UNIT = 30;

export const ScheduleFreightEdit = ({ formState, setFormState, errors }) => {
  const handleContactChange = useCallback(
    (contactType, key, value) => {
      setFormState((prevPayload) => ({
        ...prevPayload,
        [contactType]: {
          ...prevPayload[contactType],
          [key]: value,
        },
      }));
    },
    [setFormState],
  );

  const handleDeliveryNoteChange = useCallback(
    (value) => {
      setFormState((prevPayload) => ({
        ...prevPayload,
        [DELIVERY_NOTE]: value,
      }));
    },
    [setFormState],
  );

  const onChangeTime = useCallback(
    (timePickerId, time) => {
      setFormState((prev) => ({
        ...prev,
        [timePickerId]: time,
      }));
    },
    [setFormState],
  );

  const onChangeDate = useCallback(
    (dateName, date) => {
      setFormState((prev) => ({
        ...prev,
        [dateName]: date,
      }));
    },
    [setFormState],
  );

  const renderContactInputs = (contactType, contactDetails) => (
    <FlexRow className={style['contact-details-row']}>
      {Object.entries(contactDetails).map(([key, label]) => {
        const error = errors[contactType]?.[key];
        return (
          <div key={key}>
            <Input
              key={key}
              className={style['contact-input']}
              hasError={!!error}
              inlineMessage={error ? <Text value={error.message} /> : undefined}
              inlineMessageType={error && INLINE_MESSAGE_TYPES.error}
              label={<Text textType={TextType.Label} value={label} />}
              onChange={({ target }) => handleContactChange(contactType, key, target.value)}
              value={formState[contactType] ? formState[contactType][key] : ''}
              version={version2}
            />
          </div>
        );
      })}
    </FlexRow>
  );

  const isOutsideRange = useCallback((day) => {
    const isPastDay = moment().isAfter(day, 'day');
    const isWeekday = day.isoWeekday() < 6;

    // disable past days and weekends
    return isPastDay || !isWeekday;
  }, []);

  const renderDateTimePicker = (dateName, timePickerId) => (
    <FlexRow className={style['date-time-row']}>
      <FlexColumn className={style['date-column']}>
        <Text
          className={classnames({ [style['has-error']]: !!errors[dateName] })}
          textType={TextType.Label}
          value={messages.dropOffDateLabel}
        />
        <DatePicker
          calendarPosition='top-left'
          errorMessageDescriptor={errors[dateName]?.type === 'required' ? messages.requiredError : undefined}
          hasError={!!errors[dateName]}
          isOutsideRange={isOutsideRange}
          name={dateName}
          onChange={(date) => onChangeDate(dateName, date)}
          value={formState[dateName]}
        />
      </FlexColumn>
      <FlexColumn className={style['time-column']}>
        <Text
          className={classnames({
            [style['has-error']]: !!errors[timePickerId],
          })}
          textType={TextType.Label}
          value={messages.dropOffTimeLabel}
        />
        <TimePicker
          disabled={!formState[dateName]}
          endTime={END_TIME}
          hasError={!!errors[timePickerId]}
          id={timePickerId}
          inlineMessage={errors[timePickerId]?.type === 'required' ? <FormattedMessage {...messages.requiredError} /> : undefined}
          minuteIncrementUnit={MINUTE_INCREMENT_UNIT}
          onTimeSelected={(time) => onChangeTime(timePickerId, time)}
          selectedValue={formState[timePickerId]}
          startTime={START_TIME}
          timeFormat='hh:mm A'
        />
      </FlexColumn>
    </FlexRow>
  );

  return (
    <>
      <Text className={style['schedule-freight-instructions']} textType={TextType.Paragraph} value={messages.scheduleFreightInstructions} />
      <FlexColumn>
        <Text className={style.title} textType={TextType.H4} value={messages.customerContactDetails} />
        {renderContactInputs(TENANT_CONTACT, {
          name: messages.customerFullNameLabel,
          email: messages.emailLabel,
          phone: messages.phoneNumberLabel,
        })}
      </FlexColumn>
      <Divider className={style.divider} />
      <FlexColumn>
        <Text className={style.title} textType={TextType.H4} value={messages.carrierContactDetails} />
        {renderContactInputs(
          CARRIER_CONTACT,
          {
            name: messages.carrierFullNameLabel,
            email: messages.emailLabel,
            phone: messages.phoneNumberLabel,
          },
          errors,
        )}
      </FlexColumn>
      <FlexRow>
        <div className={style['delivery-notes-input']}>
          <Text className={style.title} textType={TextType.Label} value={messages.deliveryNotesLabel} />
          <FloatTextArea onChange={handleDeliveryNoteChange} value={formState.deliveryNote} />
        </div>
      </FlexRow>
      <div className={style['line-break']} />
      <FlexRow>
        <Text className={style.title} textType={TextType.H4} value={messages.dropOffTimeSlotLabel} />
        <Text textType={TextType.Paragraph} value={messages.dropOffTimeSlotInstructions} />
      </FlexRow>
      <FlexRow>
        <BannerMessageV2
          className={style['drop-off-info-banner']}
          content={<Text textType={TextType.Caption} value={messages.dropOffTimeSlotInfo} />}
          status={BannerStatus.Info}
        />
      </FlexRow>
      {renderDateTimePicker('dropOffDateOptionOne', 'timePickerOne')}
      {renderDateTimePicker('dropOffDateOptionTwo', 'timePickerTwo')}
      {renderDateTimePicker('dropOffDateOptionThree', 'timePickerThree')}
    </>
  );
};
